/**
 * The translation progress of an editing screen, kept in step with the form.
 *
 * The server renders how much of the content is written when the page is
 * built. The editor then writes a language and the bar is stale until the
 * next save, which is what this puts right: the same count, worked out from
 * what is on the screen.
 *
 * The rule is the one the server applies - a language counts once every
 * mandatory field of it says something - and the template marks those fields
 * so the two cannot drift apart.
 */

import { query, queryAll } from "../core/dom";

/** Blocks of the form, one per language. */
const BLOCKS = "[data-translation-blocks]";

/** The chip the count is drawn in. */
const PROGRESS = "[data-translation-progress]";

/** Return whether every mandatory field of `block` says something. */
export function isWritten(block: HTMLElement): boolean {
    const fields = queryAll<HTMLInputElement | HTMLTextAreaElement>("[data-translation-needed]", block);

    // A language with no mandatory field at all is written once anything in
    // it is, which is what the server means by "filled" as well.
    if (fields.length === 0) {
        const any = queryAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea", block);

        return any.some((field) => field.value.trim() !== "");
    }

    return fields.every((field) => field.value.trim() !== "");
}

/** Return how many languages of `blocks` are written. */
export function writtenCount(blocks: HTMLElement): number {
    return queryAll<HTMLElement>("[data-language]", blocks).filter(isWritten).length;
}

/** Redraw the badge of every language block. */
export function refreshBadges(blocks: HTMLElement): void {
    const done = blocks.dataset["done"] ?? "";
    const missing = blocks.dataset["missing"] ?? "";

    for (const block of queryAll<HTMLElement>("[data-language]", blocks)) {
        const badge = query<HTMLElement>("[data-translation-badge]", block);
        if (!badge) {
            continue;
        }

        const written = isWritten(block);
        badge.textContent = written ? done : missing;
        badge.classList.toggle("badge--ok", written);
        badge.classList.toggle("badge--muted", !written);
    }
}

/** Redraw the chip counting the written languages. */
export function refreshProgress(progress: HTMLElement, written: number): void {
    const total = Number.parseInt(progress.dataset["total"] ?? "0", 10);
    const bar = query<HTMLElement>(".translation-progress__fill", progress);
    const label = query<HTMLElement>(".translation-progress__label", progress);
    const pattern = progress.dataset["title"] ?? "";

    if (bar) {
        bar.style.width = `${total ? Math.round((written * 100) / total) : 0}%`;
        bar.classList.toggle("is-complete", total > 0 && written === total);
    }

    if (label) {
        label.textContent = `${written}/${total}`;
    }

    if (pattern) {
        const said = pattern.replace("{done}", String(written)).replace("{total}", String(total));
        progress.title = said;
        query<HTMLElement>(".translation-progress", progress)?.setAttribute("title", said);
    }
}

/**
 * The progress of a group of rows: a FAQ section and the questions in it.
 *
 * The bar counts what the group itself holds plus what every row of it holds,
 * so carrying a question into another section changes both of their bars. The
 * numbers travel in the markup - the group says what it is worth on its own
 * and how much one row can be worth, each row says what it holds - so the
 * count never has to be fetched.
 */
export function refreshGroup(group: HTMLElement): void {
    const bar = query<HTMLElement>("[data-progress-bar]", group);
    if (!bar) {
        return;
    }

    const base = Number.parseInt(group.dataset["progressBase"] ?? "0", 10) || 0;
    const step = Number.parseInt(group.dataset["progressStep"] ?? "0", 10) || 0;
    const rows = queryAll<HTMLElement>("[data-progress-value]", group);
    const written = rows.reduce(
        (sum, row) => sum + (Number.parseInt(row.dataset["progressValue"] ?? "0", 10) || 0),
        base,
    );

    const total = step * (1 + rows.length);
    const fill = query<HTMLElement>(".translation-progress__fill", bar);
    const label = query<HTMLElement>(".translation-progress__label", bar);

    if (fill) {
        fill.style.width = `${total ? Math.round((written * 100) / total) : 0}%`;
        fill.classList.toggle("is-complete", total > 0 && written === total);
    }

    if (label) {
        label.textContent = `${written}/${total}`;
    }
}

/** Redraw every group of the page. */
export function refreshGroups(): void {
    for (const group of queryAll<HTMLElement>("[data-progress-group]")) {
        refreshGroup(group);
    }
}

/**
 * Keep the groups in step with the lists they are drawn from.
 *
 * Dragging is what moves a row from one group into another, and it says so
 * the moment the rows change rather than after the page is loaded again.
 */
export function initGroupProgress(): void {
    if (queryAll<HTMLElement>("[data-progress-group]").length === 0) {
        return;
    }

    document.addEventListener("sortable:moved", refreshGroups);
    refreshGroups();
}

/**
 * Wire the progress of the current screen to the form under it.
 *
 * The badges of the languages follow the form whether or not the screen draws
 * a bar counting them. A screen without one - or a script which fills the
 * fields in rather than somebody typing - used to leave every language saying
 * it was missing, however much of it was written.
 */
export function initTranslationProgress(): void {
    const blocks = query<HTMLElement>(BLOCKS);
    const progress = query<HTMLElement>(PROGRESS);

    if (!blocks) {
        return;
    }

    const refresh = (): void => {
        refreshBadges(blocks);

        if (progress) {
            refreshProgress(progress, writtenCount(blocks));
        }
    };

    // `input` covers typing, `change` covers everything a script writes and
    // announces - a picture pasted into the editor, say.
    blocks.addEventListener("input", refresh);
    blocks.addEventListener("change", refresh);

    refresh();
}
